import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq, or } from 'tio-ui/utilities';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import {
  getSingleValueForTasField,
  getConfigForTasField,
  getSortedOptionsByFieldName,
  type FieldName,
  type TemplateFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { capitalizeWords } from 'tio-employee/utils/tas/textUtilities';
import Button from 'tio-reimagined/components/button';
import ReFormField from 'tio-reimagined/components/form-field';
import Input from 'tio-reimagined/components/input';
import type { InputType } from 'tio-reimagined/components/input';
import ReSelect from 'tio-reimagined/components/select';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import inputmask from 'tio-common/modifiers/inputmask';

import SchoolSelect from 'tio-common/components/tuition-assistance/program-details/school-select';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import TuitionAssistanceFormsCustomFieldsForProvince from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import { divide } from 'tio-ui/math';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { PhCalendarBlank } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/models/tas-program-instance';
import type TasUnifiedInstitutionModel from 'tio-data/models/tas-unified-institution';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type { Touched } from 'tio-common/helpers/errors-for-valibot-field';
import type { FormField } from './wizard';
import type { TasUnifiedInstitution } from 'tio-data/models/search';
import type IntlService from 'ember-intl/services/intl';
import { service } from '@ember/service';

interface StepEducationRequestSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    programTemplate: TasProgramTemplateModel;
    instance: TASProgramInstanceModel;
    instanceFields: TASProgramInstanceModelFieldsSignature;
    applicationFields: TasApplicationModel['fields'];
    startDate: string;
    endDate: string;
    selectedSchool?: TasUnifiedInstitutionModel;
    eligibility: boolean;
    eligibleBasedOnWaitingPeriod: boolean;
    isActiveParticipant: boolean;
    programFormFields: FormField[];
    customEmployeeFields: CustomFieldSignature[];
    customProgramFields: CustomFieldSignature[];
    customApplicationFields: CustomFieldSignature[];
    touched: Touched;
    startDateConfig: { visible?: boolean; required?: boolean };
    endDateConfig: { visible?: boolean; required?: boolean };
    startDateLabel: string;
    endDateLabel: string;
    startDateValidationErrors: Record<string, readonly string[] | undefined>;
    lockStatus: string;
    eligibilityWaitingPeriod: number;
    dateEligible: string;
    isLpnSchoolSelected: boolean;
    isLpnEnabled: boolean;
    showGPAField: boolean;
    typeClassification: string;
    onSaveStartDate: (dates: Date[]) => void;
    onSaveEndDate: (dates: Date[]) => void;
    onUpdateAppName: (value: string) => void;
    onSelectSchool: (school?: TasUnifiedInstitution) => void;
    onUpdateValueForField: (
      fieldName: FieldName,
      programField: FormField,
      value: string | string[] | number,
      event?: Event
    ) => void;
    onUpdateDateFieldValue: (fieldName: FieldName, dates: Date[]) => void;
    onDidUpdateEmployeeCustomFields: (fields: CustomFieldSignature[]) => void;
    onDidUpdateProgramCustomFields: (fields: CustomFieldSignature[]) => void;
    onDidUpdateApplicationCustomFields: (fields: CustomFieldSignature[]) => void;
    onSetGPA: (value: string) => void;
    inputValueForField: (field: FieldName, programField: FormField) => string;
    getSingleValueForField: (fieldName: keyof TASProgramTemplateModelFieldsSignature) => string;
    customFieldValidationErrors: { errors: string[]; value: string; path: string }[];
    onNext: () => void;
    hasAttempted: boolean;
  };
}

export default class StepEducationRequest extends Component<StepEducationRequestSignature> {
  @service declare intl: IntlService;

  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get gpaValue() {
    return String(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.args.application.fields) ?? 0
    );
  }

  private VALID_INPUT_TYPES = new Set<InputType>([
    'text',
    'email',
    'number',
    'password',
    'search',
    'tel',
    'date',
    'url',
  ]);

  asInputType = (type: string | null | undefined): InputType => {
    if (type && this.VALID_INPUT_TYPES.has(type as InputType)) {
      return type as InputType;
    }
    return 'text';
  };

  get appNameErrors() {
    if (!this.args.hasAttempted) return [];
    const value = getSingleValueForTasField('APPLICATION_NAME', this.args.application.fields);
    if (!value) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  }

  get endDateErrors() {
    if (!this.args.hasAttempted) return [];
    if (!this.args.endDateConfig.visible || !this.args.endDateConfig.required) return [];
    if (!this.args.endDate) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  }

  isFieldRequired = (fieldName: string) => {
    const config = getConfigForTasField(
      fieldName as TemplateFieldName,
      this.args.programTemplate.fields
    );
    return !!config.required;
  };

  get institutionErrors() {
    if (!this.args.hasAttempted) return [];
    const config = getConfigForTasField(
      'SCHOOL_INSTITUTION_NAME' as TemplateFieldName,
      this.args.programTemplate.fields
    );
    if (!config.required) return [];
    const value = getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME' as FieldName,
      this.args.instanceFields
    );
    if (!value) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  }

  fieldErrors = (fieldName: string, programField: FormField) => {
    if (!this.args.hasAttempted) return [];
    const config = getConfigForTasField(
      fieldName as TemplateFieldName,
      this.args.programTemplate.fields
    );
    if (!config.required || programField.disabled) return [];
    const fieldsSource =
      programField.fieldOrigination === 'application'
        ? this.args.applicationFields
        : this.args.instanceFields;
    const value = getSingleValueForTasField(fieldName as FieldName, fieldsSource);
    if (!value) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  };

  fieldConfig = (fieldName: string) => {
    return getConfigForTasField(fieldName as TemplateFieldName, this.args.programTemplate.fields);
  };

  fieldOptions = (fieldName: string) => {
    const config = this.fieldConfig(fieldName);
    if (!config.value_as_field_option) return [];
    return getSortedOptionsByFieldName(
      fieldName as TemplateFieldName,
      this.args.programTemplate.tasFieldOptions
    ).map((option) => option.value);
  };

  <template>
    <Section>
      <:header>
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.guided.steps.enter_education_request"}}</span>
        </div>
      </:header>
      <:body>
        <p class="text-sm text-gray-500 mb-6">{{t
            "tas.guided.steps.education_request_description"
          }}</p>

        {{! Education Request Name — first per Figma }}
        <Input
          @label={{t "tas.application.type_two.new.name"}}
          @isRequired={{true}}
          @value={{getSingleValueForTasField "APPLICATION_NAME" @application.fields}}
          @onChange={{@onUpdateAppName}}
          @errors={{this.appNameErrors}}
          data-test-application-name
          class="mb-6"
        />

        {{! Start Date }}
        {{#if @startDateConfig.visible}}
          <ReFormField
            @label={{capitalizeWords @startDateLabel}}
            @isRequired={{@startDateConfig.required}}
            @errors={{errorsForValibotField
              "dateEntered"
              schemaErrors=@startDateValidationErrors
              touched=@touched
              hasSubmitted=@hasAttempted
            }}
            class="mb-6"
            as |field|
          >
            <div class="relative">
              {{! @glint-expect-error: EmberFlatpickr type overrides}}
              <EmberFlatpickr
                @date={{@startDate}}
                @onChange={{@onSaveStartDate}}
                @dateFormat="Y-m-d"
                @altInput={{true}}
                @altFormat="F j, Y"
                @disableMobile={{true}}
                data-test="start"
                id={{field.fieldId}}
                placeholder="mm/dd/yyyy"
                class="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm h-10 py-2 px-3 placeholder:text-gray-400 hover:border-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-colors duration-150"
              />
              <div
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
              >
                <PhCalendarBlank @size="20" />
              </div>
            </div>
          </ReFormField>
        {{/if}}

        {{! End Date }}
        {{#if @endDateConfig.visible}}
          <ReFormField
            @label={{capitalizeWords @endDateLabel}}
            @isRequired={{@endDateConfig.required}}
            @errors={{this.endDateErrors}}
            class="mb-6"
            as |field|
          >
            <div class="relative">
              {{! @glint-expect-error: EmberFlatpickr type overrides}}
              <EmberFlatpickr
                @date={{@endDate}}
                @onChange={{@onSaveEndDate}}
                @dateFormat="Y-m-d"
                @altInput={{true}}
                @altFormat="F j, Y"
                @disableMobile={{true}}
                data-test="end"
                id={{field.fieldId}}
                placeholder="mm/dd/yyyy"
                class="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm h-10 py-2 px-3 placeholder:text-gray-400 hover:border-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-colors duration-150"
              />
              <div
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
              >
                <PhCalendarBlank @size="20" />
              </div>
            </div>
          </ReFormField>
        {{/if}}

        {{! Eligibility alerts }}
        {{#unless @eligibility}}
          <AlertBanner
            @intent="warning"
            @title={{t "start_date_eligibility.errorHeader" startEndSubmission=@lockStatus}}
            data-test-eligibility-error
            class="mb-4"
          >
            <p class="text-sm">{{t "start_date_eligibility.errorText"}}</p>
          </AlertBanner>
        {{/unless}}

        {{#unless @eligibleBasedOnWaitingPeriod}}
          <AlertBanner
            @intent="warning"
            @title={{t "tuition_assistance.program_eligibility_warnings.dashboard_title"}}
            class="mb-4"
          >
            <p class="text-sm">
              {{t
                "tuition_assistance.program_eligibility_warnings.invalid_start_date"
                time=@eligibilityWaitingPeriod
                date=@dateEligible
                htmlSafe=true
              }}
            </p>
          </AlertBanner>
        {{/unless}}

        {{#unless @isActiveParticipant}}
          <AlertBanner
            @intent="warning"
            @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
            class="my-4"
          >
            <p class="text-sm">
              {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
            </p>
          </AlertBanner>
        {{/unless}}

        {{! Institution / Program form fields — single-column full-width }}
        {{#each @programFormFields as |programField|}}
          {{#let (this.fieldConfig programField.name) as |config|}}
            {{#if (eq programField.name "SCHOOL_INSTITUTION_NAME")}}
              {{#if config.visible}}
                <div class="mb-6">
                  <SchoolSelect
                    @programTemplate={{@programTemplate}}
                    @labelClass="!text-gray-600 font-semibold block mb-1"
                    @selected={{@selectedSchool}}
                    @onChange={{@onSelectSchool}}
                    @errors={{this.institutionErrors}}
                    class="flex flex-col gap-1 [&_label_span]:text-red-500"
                  />
                  {{#if @isLpnSchoolSelected}}
                    <AlertBanner
                      @intent="info"
                      @title={{t "lpn.application_banner.title"}}
                      class="mt-4"
                    >
                      <p class="text-sm">{{t "lpn.application_banner.body"}}</p>
                    </AlertBanner>
                  {{/if}}
                </div>
              {{/if}}
            {{else if config.visible}}
              <div class="mb-6">
                {{#if config.value_as_field_option}}
                  <ReFormField @label={{config.label}} @isRequired={{config.required}}>
                    <ReSelect
                      data-test={{programField.name}}
                      @items={{this.fieldOptions programField.name}}
                      @selected={{@getSingleValueForField programField.name}}
                      @onChange={{fn @onUpdateValueForField programField.name programField}}
                      @disabled={{programField.disabled}}
                      @clearable={{false}}
                      @placeholder="Select an option"
                      @errors={{this.fieldErrors programField.name programField}}
                    />
                  </ReFormField>
                {{else if (eq programField.mask "currency")}}
                  <Input
                    data-test={{programField.name}}
                    @label={{config.label}}
                    @isRequired={{config.required}}
                    @value={{divide
                      (or (Number (getSingleValueForTasField programField.name @instanceFields)) 0)
                      100
                    }}
                    @onChange={{fn @onUpdateValueForField programField.name programField}}
                    @isDisabled={{programField.disabled}}
                    {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
                  />
                {{else if (eq programField.type "date")}}
                  <ReFormField @label={{config.label}} @isRequired={{config.required}} class="mb-0">
                    <div class="relative">
                      <EmberFlatpickr
                        {{! @glint-expect-error: FieldValue type is broader than DateOption }}
                        @date={{or
                          (getSingleValueForTasField programField.name @instanceFields)
                          ""
                        }}
                        @onChange={{fn @onUpdateDateFieldValue programField.name}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        @disableMobile={{true}}
                        placeholder="mm/dd/yyyy"
                        data-test={{programField.name}}
                        disabled={{programField.disabled}}
                        class="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm h-10 py-2 px-3 placeholder:text-gray-400 hover:border-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-colors duration-150"
                      />
                      <div
                        class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                      >
                        <PhCalendarBlank @size="20" />
                      </div>
                    </div>
                  </ReFormField>
                {{else}}
                  <Input
                    data-test={{programField.name}}
                    @label={{config.label}}
                    @isRequired={{config.required}}
                    @value={{@inputValueForField programField.name programField}}
                    @onChange={{fn @onUpdateValueForField programField.name programField}}
                    @type={{this.asInputType programField.type}}
                    @isDisabled={{programField.disabled}}
                    @errors={{this.fieldErrors programField.name programField}}
                  />
                {{/if}}
              </div>
            {{/if}}
          {{/let}}
        {{/each}}

        {{! GPA Field }}
        {{#if @showGPAField}}
          <Input
            @label={{t "tas.application.grade_point_average"}}
            @isRequired={{true}}
            @type="number"
            @value={{this.gpaValue}}
            @onChange={{@onSetGPA}}
            data-test-grade-point-average
            min="0"
            max="4.0"
            step="0.01"
            class="mb-6"
          />
        {{/if}}

        {{! Custom fields }}
        <TuitionAssistanceFormsCustomFieldsForProvince
          @province="EMPLOYEE"
          @customFields={{@customEmployeeFields}}
          @didUpdateFields={{@onDidUpdateEmployeeCustomFields}}
          @formValidationErrors={{if @hasAttempted @customFieldValidationErrors (Array)}}
          @fieldContainerClass="mb-4"
          @programTemplate={{@programTemplate}}
        />
        <TuitionAssistanceFormsCustomFieldsForProvince
          @province="PROGRAM"
          @customFields={{@customProgramFields}}
          @didUpdateFields={{@onDidUpdateProgramCustomFields}}
          @formValidationErrors={{if @hasAttempted @customFieldValidationErrors (Array)}}
          @fieldContainerClass="mb-4"
          @programTemplate={{@programTemplate}}
        />
        <TuitionAssistanceFormsCustomFieldsForProvince
          @province="APPLICATION"
          @customFields={{@customApplicationFields}}
          @didUpdateFields={{@onDidUpdateApplicationCustomFields}}
          @formValidationErrors={{if @hasAttempted @customFieldValidationErrors (Array)}}
          @fieldContainerClass="mb-4"
          @programTemplate={{@programTemplate}}
        />

        {{! Next button }}
        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
